import React, { useState, useEffect } from 'react';
import type Tool from '../../tools/Tool';
import { type ToolArgsWithController } from '../../tools/Tool';
import { usePopper } from 'react-popper';
import styles from '../../components/ElementToolbar/ElementToolbar.module.css';
import fireToolAction from '../../tools/fireToolAction';
import WithHint from '../WithHint';
import ElementToolbarButton from './ElementToolbarButton';
import classnames from 'classnames';
import ToolButton from '../Toolbar/ToolButton';

interface IconItemsSubmenuProps extends ToolArgsWithController {
  submenuTools: (typeof Tool)[][];
}

const IconItemsSubmenu: React.FC<IconItemsSubmenuProps> = ({
  submenuTools,
  ...toolArgs
}) => {
  return (
    <>
      {submenuTools.map((toolSet: (typeof Tool)[]) => (
        <div
          key={toolSet.map((t) => t.toolName).join('-')}
          className={styles.submenuIcons}
        >
          {toolSet.map((tool: typeof Tool) => (
            <ToolButton key={tool.toolName} tool={tool} {...toolArgs} />
          ))}
        </div>
      ))}
    </>
  );
};

interface ListToggleItemProps extends ToolArgsWithController {
  tool: typeof Tool;
}

const ListToggleItem: React.FC<ListToggleItemProps> = ({
  tool,
  ...toolArgs
}) => {
  const handleClick = (event: React.MouseEvent) => {
    if (tool.isEnabled(toolArgs)) {
      fireToolAction(tool, event, toolArgs);
    } else {
      event.preventDefault();
    }
  };

  return (
    <div
      role="button"
      className={classnames(styles.submenuListToggleItem, {
        [styles.submenuListToggleItemDisabled]: !tool.isEnabled(toolArgs),
        [styles.submenuListToggleItemDanger]: tool.style === 'danger',
      })}
      onClick={handleClick}
    >
      <div
        className={classnames(
          styles.submenuListToggleItemLabel,
          tool.menuItemClass
        )}
      >
        <span className={styles.submenuListToggleItemIcon}>
          {tool.renderMenuIcon(toolArgs)}
        </span>
        <span className={styles.submenuListToggleItemText}>
          {tool.toolName}
        </span>
      </div>

      {tool.isActive(toolArgs) && (
        <div className={styles.submenuListToggleItemCheck}>
          <i className="fa-regular fa-check" />
        </div>
      )}
    </div>
  );
};

interface ListToggleItemsSubmenuProps extends ToolArgsWithController {
  submenuTools: (typeof Tool)[][];
}

const ListToggleItemsSubmenu: React.FC<ListToggleItemsSubmenuProps> = ({
  submenuTools,
  ...toolArgs
}) => {
  return (
    <>
      {submenuTools.map((toolSet: (typeof Tool)[], i) => (
        <React.Fragment key={toolSet.map((t) => t.toolName).join('-')}>
          {i > 0 && <div className={styles.submenuListToggleDivider} />}
          <div className={styles.submenuListToggle}>
            {toolSet.map((tool: typeof Tool) => (
              <ListToggleItem key={tool.toolName} tool={tool} {...toolArgs} />
            ))}
          </div>
        </React.Fragment>
      ))}
    </>
  );
};

interface ElementToolbarSubmenuProps extends ToolArgsWithController {
  tool: typeof Tool;
  toolbarShowModal: (tool: typeof Tool) => void;
  toolbarHideModal: () => void;
}

const ElementToolbarSubmenu: React.FC<ElementToolbarSubmenuProps> = ({
  tool,
  ...toolArgs
}) => {
  const [referenceElement, setReferenceElement] =
    useState<HTMLDivElement | null>(null);
  const [showSubmenu, setShowSubmenu] = useState(false);
  const [popperElement, setPopperElement] = useState<HTMLElement | null>();
  const { styles: popperStyles, attributes } = usePopper(
    referenceElement,
    popperElement,
    {
      placement: 'bottom',
      modifiers: [
        {
          name: 'preventOverflow',
          options: {
            tether: true,
            altAxis: true,
          },
        },
        {
          name: 'flip',
          options: {
            fallbackPlacements: ['top'],
          },
        },
        {
          name: 'offset',
          options: {
            offset: [0, 8], // To keep the toolbar from touching the bottom of the screen when scrolling.
          },
        },
      ],
    }
  );

  useEffect(() => {
    // Set event listener for click outside element to hide the submenu.
    const handleClickOutside = (event: MouseEvent) => {
      if (
        referenceElement &&
        !referenceElement?.contains(event.target as Node) &&
        popperElement &&
        !popperElement?.contains(event.target as Node)
      ) {
        setShowSubmenu(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [referenceElement, popperElement]);

  if (!tool.submenuTools) {
    console.error('No submenu tools found for tool');
    return null;
  }

  const submenuTools = tool.submenuTools();

  return (
    <div>
      <WithHint tool={tool} toolArgs={toolArgs}>
        <ElementToolbarButton
          tool={tool}
          toolArgs={toolArgs}
          ref={setReferenceElement}
          onClick={() => setShowSubmenu(!showSubmenu)}
        >
          {tool.renderToolbarButtonIcon(toolArgs)}
        </ElementToolbarButton>
      </WithHint>

      {showSubmenu && (
        <div
          ref={setPopperElement}
          style={{ ...popperStyles.popper, zIndex: 20 }}
          {...attributes.popper}
        >
          <div className={styles.elementToolbarCommon}>
            {tool.submenuType === 'icons' ? (
              <IconItemsSubmenu submenuTools={submenuTools} {...toolArgs} />
            ) : (
              <ListToggleItemsSubmenu
                submenuTools={submenuTools}
                {...toolArgs}
              />
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default ElementToolbarSubmenu;
